import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react";
import MaterialIcon from "./MaterialIcon";
import ProjectPicker from "./ProjectPicker";
import ProjectConfirmModal from "./ProjectConfirmModal";
import NewRoomModal from "./NewRoomModal";
import { findExactProject, findCloseProject } from "../utils/projectMatch";
import { createInternalEmailRoom, createExternalEmailRoom } from "../utils/emailAssignRoom";
import { $crud } from "../helper/CrudFactory";
import { store, UPDATE_PROJECT_ACTION_FIRE } from "../store";

/**
 * Compose project/room field: type-ahead for existing rooms, plus the same
 * "create new room" flow used when associating incoming mail (External vs
 * Internal, then company if needed).
 */
const ComposeProjectField = forwardRef(function ComposeProjectField({
  name,
  projectId,
  buildings = [],
  disabled,
  strings = {},
  onNameChange,
  onAssigned,
}, ref) {
  const [confirm, setConfirm] = useState(null);
  const [newRoom, setNewRoom] = useState(false);
  const [creating, setCreating] = useState(false);
  const pendingResolveRef = useRef(null);

  const willCreateNew = useMemo(() => {
    if (projectId) return false;
    const typed = String(name || "").trim();
    // Offer create with an empty field, and whenever the typed name is not
    // already an existing room (same rule as EmailAssignModal).
    if (!typed) return true;
    return !findExactProject(typed, buildings);
  }, [name, projectId, buildings]);

  const finishPending = (result) => {
    const resolve = pendingResolveRef.current;
    pendingResolveRef.current = null;
    if (typeof resolve === "function") resolve(result);
  };

  const applyAssigned = (building) => {
    if (!building || !building.id) return;
    if (onAssigned) onAssigned({ id: building.id, name: building.name || "" });
    try {
      store.dispatch({ type: UPDATE_PROJECT_ACTION_FIRE, updateProjectActionFire: true });
    } catch (_) { /* ignore */ }
  };

  const openNewRoom = () => {
    setConfirm(null);
    setNewRoom(true);
  };

  const startCreateFlow = () => {
    if (disabled || creating) return false;
    const typed = String(name || "").trim();
    if (projectId && !willCreateNew) {
      openNewRoom();
      return null;
    }
    if (!typed) {
      openNewRoom();
      return null;
    }
    const exact = findExactProject(typed, buildings);
    if (exact) {
      applyAssigned(exact);
      finishPending({ id: exact.id, name: exact.name });
      return true;
    }
    const close = findCloseProject(typed, buildings);
    if (close) {
      setConfirm({ suggestion: close });
      return null;
    }
    openNewRoom();
    return null;
  };

  useImperativeHandle(ref, () => ({
    ensureAssigned: () => {
      const typed = String(name || "").trim();
      if (projectId) return Promise.resolve({ id: projectId, name });
      if (!typed) return Promise.resolve(true);
      const exact = findExactProject(typed, buildings);
      if (exact) {
        applyAssigned(exact);
        return Promise.resolve({ id: exact.id, name: exact.name });
      }
      return new Promise((resolve) => {
        pendingResolveRef.current = resolve;
        startCreateFlow();
      });
    },
  }));

  const handleCreate = async (roomName, companyId, isInternal) => {
    if (creating) return;
    setCreating(true);
    try {
      const building = isInternal
        ? await createInternalEmailRoom(roomName, companyId)
        : await createExternalEmailRoom(roomName, companyId);
      applyAssigned(building);
      setNewRoom(false);
      finishPending({ id: building.id, name: building.name });
    } catch (err) {
      $crud.notify({
        type: "error",
        message: (err && err.message) || strings.something_went_wrong || "Something went wrong",
      });
      finishPending(false);
    } finally {
      setCreating(false);
    }
  };

  const closeNewRoom = () => {
    setNewRoom(false);
    finishPending(false);
  };

  const closeConfirm = () => {
    setConfirm(null);
    finishPending(false);
  };

  return (
    <div className="email-compose-project-field">
      <ProjectPicker
        value={name}
        onChange={(v) => {
          if (onNameChange) onNameChange(v);
        }}
        onSelectExisting={(b) => {
          if (onAssigned) onAssigned({ id: b.id, name: b.name || "" });
        }}
        onEnter={() => {
          if (projectId) return;
          startCreateFlow();
        }}
        buildings={buildings}
        placeholder={strings.email_compose_project_placeholder || strings.email_project_name || "Project name"}
        disabled={disabled || creating}
        strings={strings}
      />
      {willCreateNew && (
        <button
          type="button"
          className="kj-btn kj-btn--outline kj-btn--email kj-btn--sm kj-btn--soft-hover email-compose-create-room-btn"
          onClick={() => startCreateFlow()}
          disabled={disabled || creating}
          title={strings.email_account_create_room || strings.email_new_room_title || "Create new project"}
        >
          <MaterialIcon icon="add" size={15} />
          <span>{strings.email_account_create_room || strings.email_new_room_title || "Create new project"}</span>
        </button>
      )}

      {confirm && (
        <ProjectConfirmModal
          typedName={String(name || "").trim()}
          suggestion={confirm.suggestion}
          onUseExisting={() => {
            const b = confirm.suggestion;
            setConfirm(null);
            applyAssigned(b);
            finishPending({ id: b.id, name: b.name });
          }}
          onCreateNew={() => openNewRoom()}
          onClose={closeConfirm}
          strings={strings}
        />
      )}

      {newRoom && (
        <NewRoomModal
          initialName={willCreateNew ? String(name || "").trim() : ""}
          busy={creating}
          onCreate={handleCreate}
          onClose={closeNewRoom}
          strings={strings}
          submitLabel={strings.email_compose_create_room || strings.email_account_create_room || "Create project"}
          busyLabel={strings.email_compose_creating_room || "Creating project..."}
        />
      )}
    </div>
  );
});

export default ComposeProjectField;
